function QualityTierSelector({ tiers, currentDuration, onTierInfo }) {
  if (!tiers) return null;

  const tierOrder = ['basic', 'standard', 'premium'];
  
  const getCurrentTier = () => {
    if (currentDuration >= tiers.premium?.min_duration) return 'premium';
    if (currentDuration >= tiers.standard?.min_duration) return 'standard';
    if (currentDuration >= tiers.basic?.min_duration) return 'basic';
    return null;
  };

  const currentTier = getCurrentTier();

  const getTierIcon = (tierId) => {
    switch (tierId) {
      case 'premium': return '⭐';
      case 'standard': return '🎯';
      case 'basic': return '🚀';
      default: return '📦';
    }
  };

  const getTierColor = (tierId, isActive) => {
    if (!isActive) return 'border-gray-700 bg-gray-800/30 opacity-60';
    switch (tierId) {
      case 'premium': return 'border-purple-500 bg-purple-500/10';
      case 'standard': return 'border-blue-500 bg-blue-500/10';
      case 'basic': return 'border-green-500 bg-green-500/10';
      default: return 'border-gray-500 bg-gray-500/10';
    }
  };

  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    return `${Math.floor(seconds / 60)}m`;
  };

  return (
    <div className="card">
      <h3 className="font-semibold mb-2">🎚️ Quality Tiers</h3>
      <p className="text-gray-400 text-sm mb-4">
        More audio = better voice clone quality. Current: {currentDuration.toFixed(1)}s
      </p>

      <div className="space-y-3">
        {tierOrder.map((tierId) => {
          const tier = tiers[tierId];
          if (!tier) return null;

          const isUnlocked = currentDuration >= tier.min_duration;
          const isCurrentTier = currentTier === tierId;
          const progress = Math.min(100, (currentDuration / tier.min_duration) * 100);

          return (
            <div
              key={tierId}
              className={`relative rounded-lg border p-4 transition-all ${getTierColor(tierId, isUnlocked)}`}
            >
              {isCurrentTier && (
                <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
                  Current
                </div>
              )}

              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <span className="text-xl">{getTierIcon(tierId)}</span>
                  <div>
                    <h4 className="font-medium">{tier.name}</h4>
                    <p className="text-xs text-gray-500">
                      Min: {formatTime(tier.min_duration)} • 
                      Recommended: {formatTime(tier.recommended_duration)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500">Est. training</p>
                  <p className="text-sm font-mono">{formatTime(tier.estimated_training_time)}</p>
                </div>
              </div>

              <p className="text-sm text-gray-400 mb-3">{tier.description}</p>

              {!isUnlocked && (
                <div>
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>Progress to unlock</span>
                    <span>{currentDuration.toFixed(1)}s / {tier.min_duration}s</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-1.5">
                    <div
                      className="bg-gray-500 h-1.5 rounded-full transition-all"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              )}

              {isUnlocked && (
                <div className="flex items-center text-green-400 text-sm">
                  <span className="mr-1">✓</span>
                  Unlocked
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Recommendation */}
      {currentTier === 'basic' && currentDuration < 60 && (
        <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
          <p className="text-sm text-yellow-300">
            💡 <strong>Recommendation:</strong> Add {(60 - currentDuration).toFixed(0)}s more audio 
            to unlock Standard quality for much better results!
          </p>
        </div>
      )}
    </div>
  );
}

export default QualityTierSelector;
